import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Input, Output, OnInit, EventEmitter, OnDestroy } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTree, MatTreeNodeDef, MatTreeNode, MatTreeNodePadding, MatTreeNodeToggle } from '@angular/material/tree';
import { TreeNode, TreeFlatNode } from '../../../Models/tree-node';
import { SubmissionTypeConfigService } from '../submission-type-config/submission-type-config.service';
import { ModuleService } from '../../../Services/module.service';
import { ActivatedRoute } from '@angular/router';
import { TenantService } from '../../../../../../Services';
import { ParamMapService } from '../../../Services/param-map.service';
import { TreeConstants } from './tree.component.constants';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'aw-tree',
    templateUrl: './tree.component.html',
    styleUrls: ['./tree.component.less'],
    imports: [MatTree, MatTreeNodeDef, MatTreeNode, MatTreeNodePadding, MatIconButton, MatTreeNodeToggle, MatIcon]
})
export class TreeComponent implements OnInit, OnDestroy {
    @Input() treeData: TreeNode[];

    @Output() deleteNode = new EventEmitter<TreeNode>();
    @Output() addNode = new EventEmitter<TreeNode>();

    treeControl: FlatTreeControl<TreeFlatNode>;
    treeFlattener: MatTreeFlattener<TreeNode, TreeFlatNode, TreeFlatNode>;
    dataSource: MatTreeFlatDataSource<TreeNode, TreeFlatNode, TreeFlatNode>;
    selectedNode: TreeFlatNode;
    cleanTree: TreeNode[] = [];
    moduleId: string;
    version = 1;

    private isDestroyed$ = new Subject<void>();

    public constructor(
        private activatedRoute: ActivatedRoute,
        private tenantService: TenantService,
        private moduleService: ModuleService,
        private paramMapService: ParamMapService,
        private submissionTypeConfigService: SubmissionTypeConfigService) {

            const transformer = (node: TreeNode, level: number): TreeFlatNode => ({
                expandable: !!node.children && node.children.length > 0,
                name: node.name,
                uniqueIdentifier: node.uniqueIdentifier,
                level,
                parent: node.parent,
                children: node.children,
                type: node.type,
                icon: node.icon,
                allowDelete: node.allowDelete,
                allowAddChildren: node.allowAddChildren,
                isActive: node.isActive
            });
            this.treeControl = new FlatTreeControl<TreeFlatNode>(
                node => node.level, node => node.expandable);
            this.treeFlattener = new MatTreeFlattener(
                transformer, node => node.level, node => node.expandable, node => node.children);
            this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
        }

    ngOnInit(): void {
        this.moduleId = this.paramMapService.getParam(this.activatedRoute, 'moduleId');
        this.version = Number.parseInt(this.paramMapService.getParam(this.activatedRoute, 'version'), 10);

        this.submissionTypeConfigService.newNodes.pipe(
            takeUntil(this.isDestroyed$)
        ).subscribe((nodes) => this.addNewNodes(nodes));
        this.submissionTypeConfigService.deletedNode.pipe(
            takeUntil(this.isDestroyed$)
        ).subscribe(node => this.deleteNodeFromTree(node));

        this.moduleService.getSubmissionTree(this.tenantService.selectedTenant, this.moduleId, this.version)
            .then(
                tree => {
                    if (tree && tree.length > 0 && tree.find(n => n.children && n.children.length > 0)) {
                        this.treeData = tree;
                        this.cleanTree = tree;
                        this.dataSource.data = tree;
                        this.treeControl.expandAll();
                    }
                    Object.assign(this.cleanTree, this.organiseTree(this.treeData));
                    this.assignAndExpandTree();
            });
    }

    ngOnDestroy(): void {
        this.isDestroyed$.next();
    }

    getUsedNodesPerLevel(level: number): string[] {
        const result: string[] = [];
        this.filterUniqueIdentifiers(this.cleanTree, result, level);
        return result;
    }

    getUsedNodesPerIndicatorLevel(nodesToFilter: TreeNode[]): string[] {
        let usedNodes: string[] = [];

        if (nodesToFilter && nodesToFilter.length > 0) {
            usedNodes = nodesToFilter.map(node => node.uniqueIdentifier);
        }
        return usedNodes;
    }

    setActiveNode(node: TreeFlatNode): void {
        this.submissionTypeConfigService.setActiveNode(node);
        const nodesInUse: string[] = node.level === TreeConstants.indicatorLevel ? this.getUsedNodesPerIndicatorLevel(node.children)
                                                                                : this.getUsedNodesPerLevel(node.level);
        this.submissionTypeConfigService.nodesInUse.next(nodesInUse);
        this.selectedNode = node;
    }

    deleteNodeFromTree(node: TreeNode): void {
        this.traverseChildren(this.treeData, node, this.removeChild);
        this.cleanTree = this.organiseTree(this.treeData);
        this.assignAndExpandTree();
        this.moduleService.updateSubmissionTree(this.tenantService.selectedTenant, this.moduleId, this.version, this.cleanTree);
    }

    clickAddNode(node: TreeNode) {
        this.addNode.emit(node);
    }

    clickDeleteNode(node: TreeNode) {
        this.deleteNode.emit(node);
    }

    private filterUniqueIdentifiers(subTree: TreeNode[], result: string[], level: number): void {
        let process = true;
        if (subTree && process) {
            subTree.forEach(node => {
                if (node.children) {
                    if (node.level === level) {
                        node.children.map(n => { result.push(n.uniqueIdentifier); });
                        process = false;
                    } else {
                        this.filterUniqueIdentifiers(node.children, result, level);
                    }
                }
            });
        }
    }

    private assignAndExpandTree() {
        this.dataSource.data = this.cleanTree;
        this.treeControl.expandAll();
    }

    private organiseTree(tree: TreeNode[]): TreeNode[] {
        const roots: TreeNode[] = [];
            for (const node of tree) {
                if (!node.parent) {
                    roots.push(node);
                } else {
                    if (!node.parent.children) {
                        node.parent.children = [];
                    }
                    this.traverseChildren(roots, node, this.addChild);
                }
            }
        return roots;
    }

    private traverseChildren(collection: TreeNode[], node: TreeNode, actionToDo): void {
        let process = true;
        collection.forEach(x => {
            if (node.parent && x.name === node.parent.name && x.level === node.parent.level) {
                actionToDo(x, node);
                process = false;
            }
            if (x.children && process) {
                this.traverseChildren(x.children, node, actionToDo);
            }
        });
    }

    private addChild(parent: TreeNode, child: TreeNode): void {
        if (parent.children.findIndex(p => p.name === child.name && p.level === child.level) < 0) {
            parent.children.push(child);
        }
    }

    private removeChild(parent: TreeNode, child: TreeNode): void {
        const index = parent.children.findIndex(x => x.name === child.name && x.level === child.level);
        if (index > -1) {
            parent.children.splice(index, 1);
        }
    }

    private addNewNodes(nodes: TreeFlatNode[]) {
        nodes.forEach((node) => this.treeData = this.treeData.concat(node));
        this.cleanTree = this.organiseTree(this.treeData);
        this.treeData= this.cleanTree;
        this.assignAndExpandTree();
        this.moduleService.updateSubmissionTree(this.tenantService.selectedTenant, this.moduleId, this.version, this.cleanTree);
    }
}
